import { Inject, Injectable } from '@angular/core';
import {
    assertUnreachable,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    ConfigService,
    EncodingUtilities,
    ENDPOINTS,
    getOS,
    isDefined,
    MatSerNumber,
    Notification,
    NotificationIdentifier,
    NotificationInterface,
    OnlineOnlyService,
    UriUtilitiesService,
    UserMasterService,
    DeviceIdService,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { combineLatest, forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { NotificationInstanceToken } from '../core/interfaces/notification.interface';
import { FormState } from '../store/state/form.state';
import { FormIdentifier } from '../types/form-identifier';
import { FormList, FormListEntryState } from '../types/form-list';
import { FormTypeFilters, initialFormTypeFilters } from '../types/form-type-filters';
import { FormsConstants } from '../types/forms-constants';
import { FormUtilities } from '../utilities/form-utilities';
import { NotificationUtilities } from '../utilities/notification-utilities';
import {
    ODataEquipmentSet,
    ODataGetListFormModel,
    ODataGetListModel,
    ODataGetXmlModel,
    ODataPrologueModel,
} from './forms-odata.interface';
import { FormsStorageService } from './forms-storage.service';
import { XmlParserService } from './xml-parser.service';
import { XmlSerializerService } from './xml-serializer.service';
import SUBMIT_STATUS = FormsConstants.SUBMIT_STATUS;
import isAstoriaForm = FormUtilities.isAstoriaForm;

export type ODataGetListModelWithOldEntries = ODataGetListModel & {
    FormID_Navigation: { results: Array<ODataGetListFormModel & { isOldVersion?: boolean }> };
};

function getTypeFilters(data: ODataGetXmlModel): FormTypeFilters {
    return {
        [FormsConstants.ELEMENT_TYPE.A]: {
            elementType: FormsConstants.ELEMENT_TYPE.A,
            label: data.TypeALabel ? data.TypeALabel : initialFormTypeFilters[FormsConstants.ELEMENT_TYPE.A].label,
            visible: false,
            active: true,
        },
        [FormsConstants.ELEMENT_TYPE.B]: {
            elementType: FormsConstants.ELEMENT_TYPE.B,
            label: data.TypeBLabel ? data.TypeBLabel : initialFormTypeFilters[FormsConstants.ELEMENT_TYPE.B].label,
            visible: false,
            active: true,
        },
        [FormsConstants.ELEMENT_TYPE.C]: {
            elementType: FormsConstants.ELEMENT_TYPE.C,
            label: data.TypeCLabel ? data.TypeCLabel : initialFormTypeFilters[FormsConstants.ELEMENT_TYPE.C].label,
            visible: false,
            active: true,
        },
        [FormsConstants.ELEMENT_TYPE.D]: {
            elementType: FormsConstants.ELEMENT_TYPE.D,
            label: data.TypeDLabel ? data.TypeDLabel : initialFormTypeFilters[FormsConstants.ELEMENT_TYPE.D].label,
            visible: false,
            active: true,
        },
        [FormsConstants.ELEMENT_TYPE.E]: {
            elementType: FormsConstants.ELEMENT_TYPE.E,
            label: data.TypeELabel ? data.TypeELabel : initialFormTypeFilters[FormsConstants.ELEMENT_TYPE.E].label,
            visible: false,
            active: true,
        },
        [FormsConstants.ELEMENT_TYPE.F]: {
            elementType: FormsConstants.ELEMENT_TYPE.F,
            label: data.TypeFLabel ? data.TypeFLabel : initialFormTypeFilters[FormsConstants.ELEMENT_TYPE.F].label,
            visible: false,
            active: true,
        },
        [FormsConstants.ELEMENT_TYPE.STANDARD]: {
            elementType: FormsConstants.ELEMENT_TYPE.STANDARD,
            label: data.TypeStandardLabel
                ? data.TypeStandardLabel
                : initialFormTypeFilters[FormsConstants.ELEMENT_TYPE.STANDARD].label,
            visible: false,
            active: true,
        },
        [FormsConstants.ELEMENT_TYPE.QUALITY]: {
            elementType: FormsConstants.ELEMENT_TYPE.QUALITY,
            label: data.TypeQualityLabel
                ? data.TypeQualityLabel
                : initialFormTypeFilters[FormsConstants.ELEMENT_TYPE.QUALITY].label,
            visible: false,
            active: true,
        },
        [FormsConstants.ELEMENT_TYPE.SAFETY]: {
            elementType: FormsConstants.ELEMENT_TYPE.SAFETY,
            label: data.TypeSafetyLabel
                ? data.TypeSafetyLabel
                : initialFormTypeFilters[FormsConstants.ELEMENT_TYPE.SAFETY].label,
            visible: false,
            active: true,
        },
        [FormsConstants.ELEMENT_TYPE.SOFTWARE]: {
            elementType: FormsConstants.ELEMENT_TYPE.SOFTWARE,
            label: data.TypeSoftwareLabel
                ? data.TypeSoftwareLabel
                : initialFormTypeFilters[FormsConstants.ELEMENT_TYPE.SOFTWARE].label,
            visible: false,
            active: true,
        },
        [FormsConstants.ELEMENT_TYPE.TIME]: {
            elementType: FormsConstants.ELEMENT_TYPE.TIME,
            label: data.TypeTimeLabel
                ? data.TypeTimeLabel
                : initialFormTypeFilters[FormsConstants.ELEMENT_TYPE.TIME].label,
            visible: false,
            active: true,
        },
        [FormsConstants.ELEMENT_TYPE.MATERIAL]: {
            elementType: FormsConstants.ELEMENT_TYPE.MATERIAL,
            label: data.TypeMaterialLabel
                ? data.TypeMaterialLabel
                : initialFormTypeFilters[FormsConstants.ELEMENT_TYPE.MATERIAL].label,
            visible: false,
            active: true,
        },
        [FormsConstants.ELEMENT_TYPE.UNKNOWN]: {
            elementType: FormsConstants.ELEMENT_TYPE.UNKNOWN,
            label: initialFormTypeFilters[FormsConstants.ELEMENT_TYPE.UNKNOWN].label,
            visible: false,
            active: true,
        },
    };
}

@Injectable({
    providedIn: 'root',
})
export class FormsReadService {
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly translate: TranslateService,
        private readonly configService: ConfigService,
        private readonly formsStorageService: FormsStorageService,
        private readonly onlineOnlyService: OnlineOnlyService,
        @Inject(NotificationInstanceToken) private readonly notificationService: NotificationInterface,
        private readonly uriUtilities: UriUtilitiesService,
        private readonly userMasterService: UserMasterService,
        private readonly deviceIdService: DeviceIdService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
    ) {}

    /**
     * Get a list of XML forms for a given equipment. The equipmentId is a unique identifier and would in theory be
     * enough to get the materialId in the backend, but that is rather complicated so we send it to make life easier
     * in the backend.
     *
     * The backend answer contains more information but we are only interested in the array "FormID_Navigation", so
     * we extract it before handing back the result.
     *
     * @param materialId the materialId of the equipment
     * @param equipmentId the equipmentId of the equipment
     * @param notificationId the notificationId of the notification
     * @param serviceActivity the type of the form selected or assign to the user
     * @returns an observable of the form list
     */
    public getFormList(
        materialId: string,
        equipmentId: string,
        notificationId: NotificationIdentifier,
        serviceActivity?: FormsConstants.SERVICE_ACTIVITY_TYPES,
    ): Observable<FormList> {
        const notification = this.notificationService.getNotificationStateByIdentifier(notificationId);
        if (typeof notification === 'undefined') {
            return throwError(
                () =>
                    `FormsReadService: getFormList: Could not load notification '${NotificationUtilities.toString(
                        notificationId,
                    )}'`,
            );
        }

        const country = this.configService.getCseCountry();
        const languageId = notification.languageId;
        const notificationType = notification.notificationType;
        const user = this.userMasterService.getSelectedUser();

        let query = ENDPOINTS.F4mGetListSet.toString();
        let filter = `EquipmentId eq '${encodeURIComponent(equipmentId)}' and MatnrId eq '${encodeURIComponent(
            materialId,
        )}' and Country_key eq '${country}' and NotificationType eq '${notificationType}' and LanguageId eq '${languageId}'`;

        // For AA and SA, we need to add SalesOrg and Plant in the filter
        if (this.clientManager.isNgmAP0()) {
            filter = filter + ` and SalesOrg eq '${user.salesOrg}' and Plant eq '${user.plant}'`;
        }

        if (this.configService.getEnablePaceFormsCrossDeviceSync()) {
            const notificationNumber = notificationId.qmNum;
            const taskNumber = notificationId.maNum;
            filter = filter + ` and NotificationNo eq '${notificationNumber}' and TaskNo eq '${taskNumber}'`;
        }

        if (isDefined(serviceActivity)) {
            const userRole = this.userMasterService.getUserRole();
            filter = filter + ` and FormType eq '${serviceActivity}' and UserRole eq '${userRole}'`;
        }

        const expand = '$expand=FormID_Navigation';
        const format = '$format=json';

        query = this.uriUtilities.appendFilterValue(query, filter);
        query = this.uriUtilities.appendParameter(query, expand);
        query = this.uriUtilities.appendParameter(query, format);

        // $log.debug("getListOfForms: query:", query);

        return combineLatest([
            this.onlineOnlyService.get(query),
            this.formsStorageService.loadFormListOdataFromCache(materialId, equipmentId, notificationId),
        ]).pipe(
            mergeMap(([data, cachedData]: [ODataGetListModel[], ODataGetListModelWithOldEntries[]]) => {
                const mergedData = this.addOldFormVersions(cachedData, data);
                return this.formsStorageService.saveFormListOdataToCache(
                    materialId,
                    equipmentId,
                    notificationId,
                    mergedData,
                );
            }),
            catchError((error) => {
                // assume we are offline and try to load from cache
                this.log.debug(
                    `FormsReadService: getFormList: Could not read list for materialId '${materialId}', equipmentId '${equipmentId}', country '${country}', notification '${NotificationUtilities.toString(
                        notificationId,
                    )}', notificationType '${notificationType}' with error '${JSON.stringify(error)}'`,
                );
                return this.formsStorageService.loadFormListOdataFromCache(materialId, equipmentId, notificationId);
            }),
            mergeMap((data) => this.convertOdataListToState(data, notification, country, materialId, equipmentId)),
        );
    }

    private addOldFormVersions(
        oldList: ODataGetListModel[] | undefined,
        newList: ODataGetListModel[],
    ): ODataGetListModelWithOldEntries[] {
        if (!isDefined(oldList) || !isDefined(newList)) {
            return newList;
        }

        const modifiedForms = newList[0].FormID_Navigation.results.reduce((acc, curr) => {
            oldList[0].FormID_Navigation.results.forEach((entry) => {
                if (FormUtilities.isNewFormVersion(entry.FormDocid, curr.FormDocid)) {
                    acc.push({ ...entry, LockedBy: '', isOldVersion: true });
                }
            });

            acc.push({ ...curr, isOldVersion: false });
            return acc;
        }, new Array<ODataGetListFormModel & { isOldVersion: boolean }>());

        return [{ ...newList[0], FormID_Navigation: { results: modifiedForms } }];
    }

    public getFormListFromCache(
        materialId: string,
        equipmentId: string,
        notificationId: NotificationIdentifier,
    ): Observable<FormList> {
        const notification = this.notificationService.getNotificationStateByIdentifier(notificationId);
        if (typeof notification === 'undefined') {
            throw new Error(
                `FormsReadService: getFormListFromCache: Could not load notification '${NotificationUtilities.toString(
                    notificationId,
                )}'`,
            );
        }
        const country = this.configService.getCseCountry();
        return this.formsStorageService
            .loadFormListOdataFromCache(materialId, equipmentId, notificationId)
            .pipe(
                mergeMap((data) => this.convertOdataListToState(data, notification, country, materialId, equipmentId)),
            );
    }

    private convertOdataListToState(
        data: ODataGetListModelWithOldEntries[] | undefined,
        notification: Notification,
        country: string,
        materialId: string,
        equipmentId: string,
    ): Observable<FormList> {
        const notificationType = notification.notificationType;
        if (typeof data === 'undefined') {
            return throwError(
                `FormsReadService: convertOdataListToState: Could not read list for materialId '${materialId}', equipmentId '${equipmentId}', country '${country}', notification '${NotificationUtilities.toString(
                    notification.identifier,
                )}', notificationType '${notificationType}'`,
            );
        }
        if (data.length !== 1) {
            return throwError(
                `FormsReadService: convertOdataListToState: Length of data[] in response should be 1, but is ${data.length}!`,
            );
        }

        const odataEntries = data[0].FormID_Navigation.results;
        const entries = odataEntries.map((entry: ODataGetListFormModel & { isOldVersion?: boolean | undefined }) => {
            // determine type label for form list
            let typeLabel = '';
            switch (entry.Type) {
                case FormsConstants.FORMTYPE.MAINTENANCE:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_MAINTENANCE');
                    break;
                case FormsConstants.FORMTYPE.SAFETY:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_SAFETY');
                    break;
                case FormsConstants.FORMTYPE.TRAININGCHECKLIST:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_TRAININGCHECKLIST');
                    break;
                case FormsConstants.FORMTYPE.PREINSTALLATION_REPORT:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_PREINSTALLATION_REPORT');
                    break;
                case FormsConstants.FORMTYPE.INSTALLATION_REPORT:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_INSTALLATION_REPORT');
                    break;
                case FormsConstants.FORMTYPE.INSTALLATION_STARTUP_REPORT:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_INSTALLATION_STARTUP_REPORT');
                    break;
                case FormsConstants.FORMTYPE.STARTUP_REPORT:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_STARTUP_REPORT');
                    break;
                case FormsConstants.FORMTYPE.SOFTWARE_HANDLING_REPORT:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_SOFTWARE_HANDLING_REPORT');
                    break;
                case FormsConstants.FORMTYPE.TUNE_UP_REPORT:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_TUNE_UP_REPORT');
                    break;
                case FormsConstants.FORMTYPE.QUALITY_ASSURANCE_REPORT:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_QUALITY_ASSURANCE_REPORT');
                    break;
                case FormsConstants.FORMTYPE.DISASSEMBLY_REPORT:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_DISASSEMBLY_REPORT');
                    break;
                case FormsConstants.FORMTYPE.RECONDITIONING_REPORT:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_RECONDITIONING_REPORT');
                    break;
                case FormsConstants.FORMTYPE.CUSTOMER_ACCEPTANCE_REPORT:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_CUSTOMER_ACCEPTANCE_REPORT');
                    break;
                case FormsConstants.FORMTYPE.COMMISSIONING_REPORT:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_COMMISSIONING_REPORT');
                    break;
                case FormsConstants.FORMTYPE.MAINTENANCE_REPORT:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_MAINTENANCE_REPORT');
                    break;
                case FormsConstants.FORMTYPE.SAFETY_RELATED_TEST_REPORT:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_SAFETY_RELATED_TEST_REPORT');
                    break;
                case FormsConstants.FORMTYPE.APPLICATION_TRAINING_REPORT:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_APPLICATION_TRAINING_REPORT');
                    break;
                case FormsConstants.FORMTYPE.PRE_TRAINING_CLARIFICATION_REPORT:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_APPLICATION_TRAINING_REPORT');
                    break;
                case FormsConstants.FORMTYPE.TROUBLESHOOTING_REPORT:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_TROUBLESHOOTING_REPORT');
                    break;
                case FormsConstants.FORMTYPE.REPLACEMENT_REPORT:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_REPLACEMENT_REPORT');
                    break;
                case FormsConstants.FORMTYPE.ADJUSTMENT_REPORT:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_ADJUSTMENT_REPORT');
                    break;
                case FormsConstants.FORMTYPE.CONFIGURATION_REPORT:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_CONFIGURATION_REPORT');
                    break;
                case FormsConstants.FORMTYPE.SAFETY_INFORMATION_REPORT:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_SAFETY_INFORMATION_REPORT');
                    break;
                case FormsConstants.FORMTYPE.DISPOSAL_REPORT:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_DISPOSAL_REPORT');
                    break;
                case FormsConstants.FORMTYPE.SITE_PLANNING_REPORT:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_SITE_PLANNING_REPORT');
                    break;
                case FormsConstants.FORMTYPE.UPGRADE_REPORT:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_UPGRADE_REPORT');
                    break;
                case FormsConstants.FORMTYPE.UPDATE_REPORT:
                    typeLabel = <string>this.translate.instant('F4M_FORM_TYPE_UPDATE_REPORT');
                    break;
                case FormsConstants.FORMTYPE.UNKNOWN:
                    typeLabel = 'unknown';
                    break;
                default:
                    // this enforces that the switch-case covers all possibilities of the FORMTYPE enum
                    assertUnreachable(entry.Type);
            }

            const formListEntry: FormListEntryState = {
                id: { equipmentId, formId: entry.FormDocid, materialId },
                lockedBy: entry.LockedBy,
                type: entry.Type,
                typeLabel: typeLabel,
                title: entry.Title,
                variant: entry.Variante,
                loadStatus: entry.Status,
                submitStatus: SUBMIT_STATUS.UNKNOWN,
                cached: false,
                isOldVersion: !!entry.isOldVersion,
                deviceId: this.configService.getEnablePaceFormsCrossDeviceSync()
                    ? entry.DeviceId
                    : FormsConstants.NO_LOCK_DEVICE_ID,
                deviceType: this.configService.getEnablePaceFormsCrossDeviceSync()
                    ? entry.DeviceType
                    : FormsConstants.NO_LOCK_DEVICE_ID,
                previousSubmit: entry.PreviousSubmit === ConfigConstants.sapTrue,
            };

            return formListEntry;
        });

        const cachedStatus = entries.map((entry) =>
            this.formsStorageService.isFormStateInCache(entry.id, notification.identifier),
        );

        // In PACE Forms FI, we want English forms to be preselected if there are English entries.
        // In all other cases we want to display all languages that are contained in the GW result.
        const containsEnglish = entries
            .map((entry) => FormUtilities.getFormLanguage(entry.id.formId))
            .includes(FormsConstants.FORM_LANGUAGES.English);
        const selectedLanguage =
            this.clientManager.isPaceFormsFI() && containsEnglish
                ? FormsConstants.FORM_LANGUAGES.English
                : FormsConstants.FORM_LANGUAGES.Multilingual;

        // forkJoin on an empty array does not trigger the pipe by calling next(), instead it just completes()
        // and the pipe is never triggered. To fix this, we just return an empty array instead.
        const cachedStatus$ = cachedStatus.length > 0 ? forkJoin(cachedStatus) : of([]);

        return cachedStatus$.pipe(
            map((cacheResults) => {
                const updatedEntries = entries.map((entry, index) => ({
                    ...entry,
                    cached: cacheResults[index],
                }));

                const formList: FormList = {
                    isLoading: false,
                    loaded: true,
                    materialId,
                    equipmentId,
                    country,
                    notification: notification.identifier,
                    notificationType,
                    entries: updatedEntries,
                    downloadingAll: false,
                    equipmentSet: notification.equipmentSet,
                    selectedLanguage,
                };

                return formList;
            }),
        );
    }

    public getFormXml(
        id: FormIdentifier,
        notificationId: NotificationIdentifier,
        status: FormsConstants.FORMSTATUS,
    ): Observable<ODataGetXmlModel> {
        const useCustomizedForms = this.configService.getUseCustomisedForms();
        let query: string;
        let keys: string;
        const format = '$format=json';

        if (useCustomizedForms) {
            // example query string:
            // F4M_Get_Custom_XMLSet?$filter=NotificationNo eq '728100019941' and TaskNumber eq '0001' and FormDocid eq 'TEST-SLM.837.01.01.02' and Status eq ''&$format=json
            query = ENDPOINTS.F4mGetCustomXMLSet;
            keys = `NotificationNo eq '${encodeURIComponent(
                notificationId.qmNum,
            )}' and TaskNumber eq '${encodeURIComponent(notificationId.maNum)}' and FormDocid eq '${encodeURIComponent(
                id.formId,
            )}' and Status eq '${encodeURIComponent(status)}'`;
        } else {
            const notification = this.notificationService.getNotificationStateByIdentifier(notificationId);
            if (!isDefined(notification)) {
                throw new Error(
                    `FormsReadService: getFormXml: Fatal: no notification available for the notification id '${JSON.stringify(
                        notificationId,
                    )}`,
                );
            }

            const mainEquipment = this.notificationService.getMainEquipmentFromNotification(notification);
            if (!isDefined(mainEquipment)) {
                throw new Error(
                    `FormsReadService: getFormXml: Fatal: no mainEquipment available for notification '${JSON.stringify(
                        notificationId,
                    )}`,
                );
            }

            // "F4M_Get_XMLSet(EquipmentId='1010444202',MatnrId='Material_000000002',FormDocid='M7-040.832.05.01.02', NotificationType='MA')?$format=json"
            query = ENDPOINTS.F4mGetXMLSet;
            keys = `EquipmentId eq '${encodeURIComponent(id.equipmentId)}' and MatnrId eq '${encodeURIComponent(
                id.materialId,
            )}' and FormDocid eq '${encodeURIComponent(id.formId)}' and Status eq '${encodeURIComponent(
                status,
            )}' and SernrId eq '${encodeURIComponent(mainEquipment.sernr)}'`;
        }

        if (this.configService.getEnablePaceFormsCrossDeviceSync()) {
            const deviceType = getOS();
            const deviceId = this.deviceIdService.getLegacyDeviceId(this.configService.getUserFrontId());
            keys = keys + ` and DeviceType eq '${deviceType}' and DeviceId eq '${deviceId}'`;
        }
        query = this.uriUtilities.appendFilterValue(query, keys);
        query = this.uriUtilities.appendParameter(query, format);

        return this.onlineOnlyService.get(query).pipe(
            mergeMap((data: ODataGetXmlModel[]) => {
                const safetyAssessmentFormId = this.configService.getSafetyAssessmentForm();

                if (FormUtilities.isSafetyAssessmentForm(id.formId, safetyAssessmentFormId)) {
                    return this.formsStorageService.saveSAFXmlToCache(id, notificationId, data[0]);
                }
                return this.formsStorageService.saveFormXmlToCache(id, notificationId, data[0]);
            }),
            catchError((error) => {
                this.log.debug(
                    `FormsReadService: getFormXml: Could not load xml for formId '${JSON.stringify(
                        id,
                    )}', notification '${JSON.stringify(notificationId)}' with error '${JSON.stringify(error)}'`,
                );
                return this.formsStorageService.loadFormXmlFromCache(id, notificationId);
            }),
            map((data) => {
                if (typeof data === 'undefined') {
                    throw new Error(
                        `FormsReadService: getFormXml: Fatal: Could not load xml for formId '${JSON.stringify(
                            id,
                        )}', notification '${JSON.stringify(notificationId)}' (backend not reachable and not in cache)`,
                    );
                }
                return data;
            }),
        );
    }

    /**
     * Load the XML of a specific form for a specific equipment. MaterialId is to make life in the backend easier.
     *
     * @param id the formId
     * @param notificationId the notificationId
     * @param status the status of the form
     * @returns an observable of the form
     */
    public getForm(
        id: FormIdentifier,
        notificationId: NotificationIdentifier,
        status: FormsConstants.FORMSTATUS,
    ): Observable<{
        partialFormState: Pick<
            FormState,
            | 'formId'
            | 'notification'
            | 'title'
            | 'variant'
            | 'language'
            | 'type'
            | 'lockedBy'
            | 'deviceId'
            | 'lockedOn'
            | 'saveMode'
            | 'radioButtonLabels'
            | 'typeFilters'
            | 'doctype'
            | 'escalationEmail'
        >;
        xml: string;
    }> {
        const notification = this.notificationService.getNotificationStateByIdentifier(notificationId);
        if (typeof notification === 'undefined') {
            return throwError(
                `FormsReadService: getForm: Could not get notification model for id '${JSON.stringify(notificationId)}'`,
            );
        }

        return this.getFormXml(id, notificationId, status).pipe(
            map((data) => {
                if (!data.RadioButton1Label || !data.RadioButton2Label || !data.RadioButton3Label) {
                    this.log.warn(
                        `FormsReadService: getForm: Radio button labels are missing: yes = '${data.RadioButton1Label}', no = '${data.RadioButton2Label}', n.a. = '${data.RadioButton3Label}'`,
                    );
                }
                const lockedOnTimestamp = data.LockedOn ? data.LockedOn.replace(/\/Date\((\d+)\)\//, '$1') : '';
                const lockedOnDate =
                    lockedOnTimestamp.length > 0 ? moment(parseInt(lockedOnTimestamp, 10)) : moment.invalid();

                const type = data.Type as FormsConstants.FORMTYPE;

                if (!Object.values(FormsConstants.FORMTYPE).includes(type)) {
                    throw new Error(`FormsReadService: getForm: Form type '${data.Type}' is unknown!`);
                }

                let xml = EncodingUtilities.base64DecodeUnicode(data.Content);

                let doctype: string | undefined;

                if (isAstoriaForm(type)) {
                    // Old interim files created by legacy PACE Forms might contain PDARESULT tags instead of PDFFORM tags.
                    // We take these and replace them with PDFFORM again as otherwise the PDF generator will fail.
                    xml = xml.replace(/pdaresult/gi, 'PDFFORM');

                    // ensure that we have an unmodified copy of the doctype with its entity definitions
                    xml = XmlSerializerService.fixBackslashesInEntities(xml);
                    doctype = XmlParserService.getDoctype(xml);
                }

                return {
                    xml,
                    partialFormState: {
                        formId: id,
                        notification: notificationId,
                        title: data.Title,
                        variant: data.Variante || '',
                        language: notification.languageId,
                        type,
                        lockedBy: data.LockedBy,
                        deviceId: this.configService.getEnablePaceFormsCrossDeviceSync()
                            ? data.DeviceId
                            : FormsConstants.NO_LOCK_DEVICE_ID,
                        lockedOn: lockedOnDate.isValid() ? lockedOnDate.toISOString() : '',
                        saveMode: data.Savemode,
                        radioButtonLabels: {
                            yes: data.RadioButton1Label || FormsConstants.RADIOBUTTON_LABELS.YES,
                            no: data.RadioButton2Label || FormsConstants.RADIOBUTTON_LABELS.NO,
                            na: data.RadioButton3Label || FormsConstants.RADIOBUTTON_LABELS.NA,
                        },
                        typeFilters: getTypeFilters(data),
                        doctype,
                        escalationEmail: data.EscalationEmail || this.configService.getEscalationEmail(),
                    },
                };
            }),
        );
    }

    public getPrologue(type: FormsConstants.FORMTYPE, _language: string): Observable<string> {
        // example query string:
        // F4M_Get_PrologueSet(ProtocolType='832',CountryKey='DE')?$format=json
        let query = ENDPOINTS.F4mGetPrologueSet.toString();
        const language = _language.toUpperCase();
        const parameters = `(ProtocolType='${encodeURIComponent(type.toString())}',CountryKey='${encodeURIComponent(
            language,
        )}')`;
        const format = '$format=json';

        // query = this.uriUtilities.appendFilterValue(query, filter);
        query = this.uriUtilities.appendParameter(query, parameters);
        query = this.uriUtilities.appendParameter(query, format);

        return this.onlineOnlyService.get(query).pipe(
            // currently the service returns an array containing all prologues for all form types and languages.
            // In the future it will support filters and only deliver the matching data, so we add some code to
            // react accordingly.
            map((data: ODataPrologueModel[] | ODataPrologueModel) => {
                const prologue = Array.isArray(data)
                    ? data.find((entry) => entry.CountryKey.toUpperCase() === language && entry.ProtocolType === type)
                    : data;

                if (!isDefined(prologue)) {
                    throw new Error(
                        `FormsReadService: getPrologue: Data set did not contain prologue matching criteria language '${language}' and form type '${type}'`,
                    );
                }
                return prologue;
            }),
            mergeMap((data) => this.formsStorageService.savePrologueOdataToCache(type, language, data)),
            catchError((error) => {
                this.log.debug(
                    `FormsReadService: getPrologue: Could not read prologue for type '${type}', language '${language}' with error '${JSON.stringify(
                        error,
                    )}'`,
                );
                return this.formsStorageService.loadPrologueOdataFromCache(type, language);
            }),
            map((data) => {
                if (typeof data === 'undefined') {
                    throw new Error(`FormsReadService: getPrologue: failed to load prologue from gateway and cache!`);
                }

                return EncodingUtilities.base64DecodeUnicode(data.Base64);
            }),
        );
    }

    public getEquipmentSetForNotification(
        notificationId: NotificationIdentifier,
        mainEquipment: MatSerNumber,
    ): Observable<MatSerNumber[]> {
        let query = ENDPOINTS.MaterialSerialNumbersGet.toString();
        const filter = `Material eq '${encodeURIComponent(mainEquipment.matnr)}' and SerialNo eq '${encodeURIComponent(
            mainEquipment.sernr,
        )}'`;
        query = this.uriUtilities.appendFilterValue(query, filter);
        query = this.uriUtilities.appendParameter(query, '$format=json');

        return this.onlineOnlyService.get(query).pipe(
            mergeMap((data: ODataEquipmentSet[]) =>
                this.formsStorageService.saveEquipmentSetODataToCache(notificationId, data),
            ),
            catchError((error) => {
                // assume we are offline and try to load from cache
                this.log.debug(
                    `FormsReadService: getEquipmentSetForNotification: Could not read equipmentSet for notification '${NotificationUtilities.toString(
                        notificationId,
                    )}' and main equipment '${mainEquipment}' with error '${JSON.stringify(error)}'`,
                );
                return this.formsStorageService.loadEquipmentSetOdataFromCache(notificationId);
            }),
            map((data) => this.convertODataEquipmentSetToState(data)),
        );
    }

    private convertODataEquipmentSetToState(data: ODataEquipmentSet[] | undefined): MatSerNumber[] {
        if (!isDefined(data)) {
            return [];
        }
        return data.map((equipmentSet) => ({
            matnr: equipmentSet.Material,
            sernr: equipmentSet.SerialNo,
            description: equipmentSet.Description,
        }));
    }
}
